/**
 * Ölçüm kartı (stat tile) — **uzuv** mertebesi.
 *
 * Vuexy deseni: sol tarafta etiket + büyük sayı + alt açıklama, sağda
 * **yumuşak zeminli daire içinde ikon**. İkonun daire içinde olması onu
 * dekorasyondan çıkarıp bir işaret hâline getiriyor; çıplak ikon kartın
 * köşesinde asılı kalıyor.
 *
 * Kullanım kuralı (docs/13: "her şey kart değildir"): bu bileşen yalnızca
 * **sayının kendisi sayfanın konusu olduğunda** kullanılır. Bir listenin
 * yanına "toplam 105" yazmak için kart açılmaz.
 *
 * `vurgulu` yalnızca müdahale gerektiren sayı için: dikkat isteyen fatura
 * varsa o kart kırmızıya döner. İki kart aynı anda vurgulu olursa vurgunun
 * anlamı kalmaz.
 */

import type { ReactNode } from 'react';
import { cn } from '../../lib/cn';

export type OlcumTonu = 'notr' | 'ok' | 'uyari' | 'kotu' | 'bilgi';

/** Ton → ikon dairesi. Kart zemini nötr kalır; renk yalnızca işarette. */
const IKON_TONU: Record<OlcumTonu, string> = {
  notr: 'bg-[var(--surface-sunken)] text-[var(--ink-muted)]',
  ok: 'bg-[var(--state-ok-soft)] text-[var(--state-ok)]',
  uyari: 'bg-[var(--state-wait-soft)] text-[var(--state-wait)]',
  kotu: 'bg-[var(--state-bad-soft)] text-[var(--state-bad)]',
  bilgi: 'bg-[var(--state-info-soft)] text-[var(--state-info)]',
};

export interface OlcumProps {
  etiket: string;
  /** Büyük sayı. Zaten biçimlendirilmiş metin bekler (format.ts). */
  deger: string;
  /** Sayının altındaki tek satır bağlam. */
  altMetin?: string | undefined;
  ikon: ReactNode;
  ton?: OlcumTonu;
  /**
   * Müdahale gerektiren sayı — kartın tamamı uyarı rengine döner.
   * Sayfada EN FAZLA BİR kart vurgulu olmalı.
   */
  vurgulu?: boolean;
  /** Tıklanabilirse (ör. "hatalı faturaları göster"). */
  onTikla?: (() => void) | undefined;
}

export function Olcum({ etiket, deger, altMetin, ikon, ton = 'notr', vurgulu = false, onTikla }: OlcumProps) {
  const govde = (
    <>
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-1">
          <span className="ust-etiket">{etiket}</span>
          <span
            className={cn(
              'rakam text-2xl leading-none font-semibold',
              vurgulu ? 'text-[var(--state-bad)]' : 'text-[var(--ink)]'
            )}
          >
            {deger}
          </span>
        </div>

        {/* İkon dairesi — yarıçap full, dolgu 12px (parmak mertebesi). */}
        <span
          className={cn(
            'inline-flex size-10 shrink-0 items-center justify-center rounded-full',
            vurgulu ? IKON_TONU.kotu : IKON_TONU[ton]
          )}
          aria-hidden="true"
        >
          {ikon}
        </span>
      </div>

      {altMetin && <span className="text-xs text-[var(--ink-muted)]">{altMetin}</span>}
    </>
  );

  const sinif = cn(
    'kap flex flex-col gap-3 p-6 text-left',
    // Normal kart `.uzuv` yüzeyini kullanır; vurgulu olan kendi rengini alır
    // ve kenarlıkla işaretlenir — gölge tek başına "dikkat" diyemez.
    vurgulu
      ? 'rounded-[var(--radius-lg)] border border-[var(--state-bad)] bg-[var(--state-bad-soft)]'
      : 'uzuv',
    onTikla && 'transition-shadow hover:shadow-[var(--shadow-float)]'
  );

  if (onTikla) {
    return (
      <button type="button" onClick={onTikla} className={sinif}>
        {govde}
      </button>
    );
  }
  return <div className={sinif}>{govde}</div>;
}

/**
 * Ölçüm satırı — kartları eşit ızgarada dizer.
 *
 * Sütun sayısı **kaba** göre belirlenir (ekrana değil): aynı satır dar bir
 * panelde de doğru davransın. Kartlar arası boşluk 16px; kart iç dolgusu da
 * 16px — kural gereği iç dolgu kardeş boşluğunu geçemez (docs/13 bölüm 2.4).
 */
export function OlcumSatiri({ children }: { children: ReactNode }) {
  return <div className="grid grid-cols-1 gap-4 @2xl:grid-cols-2 @5xl:grid-cols-4">{children}</div>;
}
